import axios from "axios";
import { store, DEMO_USER } from "./mockData";
import { flattenValidationDetail } from "../utils/apiErrors";
import { safeBlob } from "../utils/safeBlob";
import { ticketStatusLabel } from "../components/ticketStatusVocabulary";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (typeof config.headers?.delete === "function") {
      config.headers.delete("Content-Type");
    } else if (config.headers) {
      delete config.headers["Content-Type"];
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) error.response.data = flattenValidationDetail(error.response.data);
    // FastAPI's HTTPBearer answers a request WITHOUT an Authorization header with 403 "Not authenticated", not 401. A tab whose
    // token was removed by another tab (that tab hit the 02:00 expiry, or the user logged out there) sends no header, so it
    // never signed out and kept erroring until a manual reload.
    const status = error.response?.status;
    if (status === 401 || (status === 403 && error.response?.data?.detail === "Not authenticated")) {
      // Only the session: localStorage.clear() also erased show_only_mine, the page sizes and the sidebar state, so
      // they reset every time the daily 02:00 token expiry hit.
      localStorage.removeItem("access_token");
      localStorage.removeItem("demo_mode");
      sessionStorage.clear();
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

const isDemo = () => localStorage.getItem("demo_mode") === "true";
const ok = (data) => Promise.resolve(data);

const appendFiles = (formData, files = []) => {
  [...files].forEach((file) => formData.append("files", file));
};

export const buildProjectNoteFormData = (message, files = []) => {
  const form = new FormData();
  form.append("message", message);
  appendFiles(form, files);
  return form;
};

const addHistory = (ticketId, event, label) => {
  const entry = {
    id: `h-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    ticket_id: ticketId,
    event,
    label,
    actor: DEMO_USER.display_name,
    created_at: new Date().toISOString(),
  };
  store.history[ticketId] = [...(store.history[ticketId] ?? []), entry];
};

// ─── Tickets ──────────────────────────────────────────────────────────────────

export const ticketsApi = {
  list: (params) => {
    if (isDemo()) {
      let result = [...store.tickets];
      if (params?.status)        result = result.filter((t) => t.status === params.status);
      if (params?.open_only)     result = result.filter((t) => ["open", "in_progress", "pending", "waiting"].includes(t.status));
      if (params?.priority)      result = result.filter((t) => t.priority === params.priority);
      if (params?.company_id)    result = result.filter((t) => t.company_id === params.company_id);
      if (params?.technician_id) result = result.filter((t) => t.technician_id === params.technician_id);
      if (params?.sla_breach !== undefined) result = result.filter((t) => t.is_sla_breach === params.sla_breach);
      if (params?.search) {
        const s = params.search.toLowerCase();
        result = result.filter((t) =>
          t.title.toLowerCase().includes(s) ||
          t.sender_name.toLowerCase().includes(s) ||
          t.sender_email.toLowerCase().includes(s)
        );
      }
      return ok(result);
    }
    return api.get("/api/tickets/", { params }).then((r) => r.data);
  },

  get: (id) => {
    if (isDemo()) {
      const ticket = store.tickets.find((t) => t.id === id);
      return ticket ? ok(ticket) : Promise.reject(new Error("Not found"));
    }
    return api.get(`/api/tickets/${id}`).then((r) => r.data);
  },

  knowledgeCandidates: (params) =>
    api.get("/api/tickets/knowledge/candidates", { params }).then((r) => r.data),


  create: (data) => {
    if (isDemo()) {
      const tech    = data.technician_id ? store.technicians.find((t) => t.id === data.technician_id) : null;
      const company = data.company_id    ? store.companies.find((c) => c.id === data.company_id)      : null;
      const newTicket = {
        ...data,
        id: `tk-${Date.now()}`,
        ticket_number: 1000 + store.tickets.length + 1,
        status: data.status ?? "open",
        company_id: data.company_id ?? null,
        company, technician: tech,
        opened_at:  new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_sla_breach: false,
      };
      store.tickets.unshift(newTicket);
      addHistory(newTicket.id, "created", "קריאה נפתחה");
      return ok(newTicket);
    }
    return api.post("/api/tickets/", data).then((r) => r.data);
  },

  update: (id, data) => {
    if (isDemo()) {
      const idx = store.tickets.findIndex((t) => t.id === id);
      if (idx >= 0) {
        const old = store.tickets[idx];
        const updated = { ...old, ...data, updated_at: new Date().toISOString() };

        if ("technician_id" in data) {
          updated.technician = data.technician_id
            ? store.technicians.find((t) => t.id === data.technician_id) ?? null
            : null;
        }
        if ("company_id" in data) {
          updated.company = data.company_id
            ? store.companies.find((c) => c.id === data.company_id) ?? null
            : null;
        }

        store.tickets[idx] = updated;

        if (data.status && data.status !== old.status) {
          addHistory(id, "status_changed", `סטטוס שונה ל: ${ticketStatusLabel(data.status)}`);
        }
        if ("technician_id" in data && data.technician_id !== old.technician_id) {
          const tech = updated.technician;
          addHistory(id, "technician_assigned",
            tech ? `טכנאי שובץ: ${tech.first_name} ${tech.last_name}` : "שיבוץ טכנאי הוסר");
        }
        if ("company_id" in data && data.company_id !== old.company_id) {
          const comp = updated.company;
          addHistory(id, "company_assigned",
            comp ? `חברה שויכה: ${comp.name}` : "שיוך חברה הוסר");
        }

        return ok(store.tickets[idx]);
      }
    }
    return api.patch(`/api/tickets/${id}`, data).then((r) => r.data);
  },

  delete: (id) => {
    if (isDemo()) {
      store.tickets = store.tickets.filter((t) => t.id !== id);
      return ok(null);
    }
    return api.delete(`/api/tickets/${id}`);
  },

  bulkDelete: (ticketIds) => {
    if (isDemo()) {
      const ids = new Set(ticketIds);
      store.tickets = store.tickets.filter((t) => !ids.has(t.id));
      return ok(null);
    }
    return api.post("/api/tickets/bulk-delete", { ticket_ids: ticketIds });
  },

  bulkUpdateStatus: (ticketIds, status) => {
    if (isDemo()) {
      const ids = new Set(ticketIds);
      store.tickets = store.tickets.map((t) => {
        if (!ids.has(t.id)) return t;
        if (t.status !== status) {
          addHistory(t.id, "status_changed", `סטטוס שונה ל: ${ticketStatusLabel(status)}`);
        }
        return { ...t, status, has_customer_reply: false, updated_at: new Date().toISOString() };
      });
      return ok(null);
    }
    return api.patch("/api/tickets/bulk/status", { ticket_ids: ticketIds, status });
  },

  bulkAssign: (ticketIds, technicianId) => {
    if (isDemo()) {
      const ids = new Set(ticketIds);
      store.tickets = store.tickets.map((t) => {
        if (!ids.has(t.id)) return t;
        const tech = store.technicians.find((item) => item.id === technicianId) || null;
        addHistory(t.id, "technician_assigned", tech ? `טכנאי שובץ: ${tech.first_name} ${tech.last_name}` : "טכנאי שובץ");
        return {
          ...t,
          technician_id: technicianId,
          technician: tech,
          status: ["open", "unassigned"].includes(t.status) ? "in_progress" : t.status,
          updated_at: new Date().toISOString(),
        };
      });
      return ok(null);
    }
    return api.post("/api/tickets/bulk/assign", { ticket_ids: ticketIds, technician_id: technicianId });
  },

  merge: (primaryId, secondaryIds) => {
    if (isDemo()) {
      const ids = new Set(secondaryIds);
      store.tickets = store.tickets.map((t) =>
        ids.has(t.id) ? { ...t, status: "merged", merged_into_id: primaryId } : t
      );
      return ok(store.tickets.find((t) => t.id === primaryId));
    }
    return api.post("/api/tickets/merge", { primary_id: primaryId, secondary_ids: secondaryIds }).then((r) => r.data);
  },

  listMerged: (ticketId) => {
    if (isDemo()) return ok([]);
    return api.get(`/api/tickets/${ticketId}/merged`).then((r) => r.data);
  },

  split: ({ source_ticket_id, new_subject, note, technician_id, email_message_id }) => {
    if (isDemo()) return ok(null);
    return api.post("/api/tickets/split", { source_ticket_id, new_subject, note, technician_id, email_message_id }).then((r) => r.data);
  },

  moveQueue: (id, data) => api.put(`/api/tickets/${id}/move-queue`, data).then((r) => r.data),
  suggestAssignee: (id) => api.get(`/api/tickets/${id}/suggest-assignee`).then((r) => r.data),
};

// ─── Technicians ──────────────────────────────────────────────────────────────

export const techniciansApi = {
  list: () => {
    if (isDemo()) return ok(store.technicians);
    return api.get("/api/technicians/").then((r) => r.data);
  },
  online: () => {
    if (isDemo()) {
      const now = new Date();
      const demo = store.technicians.map((t, i) => ({
        ...t,
        last_seen: i === 0
          ? new Date(now - 5 * 60000).toISOString()
          : i === 1
          ? new Date(now - 2 * 3600000).toISOString()
          : null,
      }));
      return ok(demo);
    }
    return api.get("/api/technicians/online").then((r) => r.data);
  },
  get: (id) => {
    if (isDemo()) return ok(store.technicians.find((t) => t.id === id));
    return api.get(`/api/technicians/${id}`).then((r) => r.data);
  },
  create: (data) => {
    if (isDemo()) {
      const t = { ...data, id: `tch-${Date.now()}`, created_at: new Date().toISOString() };
      store.technicians.push(t);
      return ok(t);
    }
    return api.post("/api/technicians/", data).then((r) => r.data);
  },
  update: (id, data) => {
    if (isDemo()) {
      const idx = store.technicians.findIndex((t) => t.id === id);
      if (idx >= 0) store.technicians[idx] = { ...store.technicians[idx], ...data };
      return ok(store.technicians[idx]);
    }
    return api.patch(`/api/technicians/${id}`, data).then((r) => r.data);
  },
  delete: (id) => {
    if (isDemo()) { store.technicians = store.technicians.filter((t) => t.id !== id); return ok(null); }
    return api.delete(`/api/technicians/${id}`);
  },
};

// ─── Companies ────────────────────────────────────────────────────────────────

export const companiesApi = {
  list: () => {
    if (isDemo()) return ok(store.companies);
    return api.get("/api/companies/").then((r) => r.data);
  },
  get: (id) => {
    if (isDemo()) return ok(store.companies.find((c) => c.id === id));
    return api.get(`/api/companies/${id}`).then((r) => r.data);
  },
  create: (data) => {
    if (isDemo()) {
      const c = { ...data, id: `co-${Date.now()}`, created_at: new Date().toISOString() };
      store.companies.push(c);
      return ok(c);
    }
    return api.post("/api/companies/", data).then((r) => r.data);
  },
  update: (id, data) => {
    if (isDemo()) {
      const idx = store.companies.findIndex((c) => c.id === id);
      if (idx >= 0) store.companies[idx] = { ...store.companies[idx], ...data };
      return ok(store.companies[idx]);
    }
    return api.patch(`/api/companies/${id}`, data).then((r) => r.data);
  },
  delete: (id) => {
    if (isDemo()) { store.companies = store.companies.filter((c) => c.id !== id); return ok(null); }
    return api.delete(`/api/companies/${id}`);
  },
};

// ─── Comments ─────────────────────────────────────────────────────────────────

export const commentsApi = {
  list: (ticketId) => {
    if (isDemo()) return ok(store.comments[ticketId] ?? []);
    return api.get(`/api/comments/tickets/${ticketId}/comments`).then((r) => r.data);
  },
  create: (ticketId, data) => {
    const technicianIds = data.additional_technician_ids?.length
      ? data.additional_technician_ids
      : data.additional_technician_id
      ? [data.additional_technician_id]
      : [];
    if (isDemo()) {
      const ticket = store.tickets.find((t) => t.id === ticketId);
      const assignedTech = ticket?.technician ?? null;
      const notificationMetadata = technicianIds.map((techId) => {
        const extraTech = store.technicians.find((t) => t.id === techId) ?? null;
        const sameAsAssigned =
          !!extraTech &&
          !!assignedTech &&
          (extraTech.id === assignedTech.id || (extraTech.email || "").toLowerCase() === (assignedTech.email || "").toLowerCase());
        return {
          additional_technician_id: techId,
          additional_technician_name: extraTech ? `${extraTech.first_name} ${extraTech.last_name}` : null,
          additional_technician_email: extraTech?.email ?? null,
          notification_status: extraTech && !sameAsAssigned && extraTech.email ? "sent" : "skipped",
          sent_at: extraTech && !sameAsAssigned && extraTech.email ? new Date().toISOString() : null,
          skip_reason: extraTech
            ? sameAsAssigned
              ? "same_as_assigned"
              : extraTech.email
              ? null
              : "no_email"
            : "technician_not_found",
        };
      });
      const sentEmailRecipients = [];
      const seenRecipientEmails = new Set();
      notificationMetadata.forEach((entry) => {
        const email = entry.additional_technician_email?.trim().toLowerCase();
        if (entry.notification_status !== "sent" || !email || seenRecipientEmails.has(email)) return;
        seenRecipientEmails.add(email);
        sentEmailRecipients.push({
          display_name: entry.additional_technician_name,
          email,
        });
      });
      const c = {
        ...data,
        id: `cm-${Date.now()}`,
        ticket_id: ticketId,
        author_id: DEMO_USER.id,
        author_name: DEMO_USER.display_name,
        author: { display_name: DEMO_USER.display_name },
        notification_metadata: notificationMetadata.length ? notificationMetadata : null,
        email_recipients_snapshot: {
          status: sentEmailRecipients.length ? "sent" : "not_sent",
          recipients: sentEmailRecipients,
          attempts: notificationMetadata.map((entry) => ({
            display_name: entry.additional_technician_name,
            email: entry.additional_technician_email,
            notification_status: entry.notification_status,
            skip_reason: entry.skip_reason,
            sent_at: entry.sent_at,
          })),
          captured_at: new Date().toISOString(),
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      store.comments[ticketId] = [...(store.comments[ticketId] ?? []), c];
      addHistory(ticketId, "comment_added", data.is_internal ? "הערה פנימית נוספה" : "הערה נוספה");
      return ok(c);
    }
    if (data.files?.length) {
      const formData = new FormData();
      formData.append("body", data.body);
      formData.append("is_internal", String(data.is_internal ?? false));
      technicianIds.forEach((techId) => formData.append("additional_technician_ids", techId));
      appendFiles(formData, data.files);
      return api.post(`/api/comments/tickets/${ticketId}/comments`, formData).then((r) => r.data);
    }
    return api
      .post(`/api/comments/tickets/${ticketId}/comments`, {
        body: data.body,
        is_internal: data.is_internal,
        additional_technician_ids: technicianIds,
      })
      .then((r) => r.data);
  },
  update: (ticketId, commentId, data) => {
    if (isDemo()) {
      const list = store.comments[ticketId] ?? [];
      const idx = list.findIndex((c) => c.id === commentId);
      if (idx === -1) return ok(null);
      const updated = {
        ...list[idx],
        body: data.body,
        body_html: data.body,
        edited_at: new Date().toISOString(),
        edited_by_name: DEMO_USER.display_name,
      };
      store.comments[ticketId] = [...list.slice(0, idx), updated, ...list.slice(idx + 1)];
      return ok(updated);
    }
    return api.patch(`/api/comments/tickets/${ticketId}/comments/${commentId}`, data).then((r) => r.data);
  },
  delete: (ticketId, commentId) => {
    if (isDemo()) {
      store.comments[ticketId] = (store.comments[ticketId] ?? []).filter((comment) => comment.id !== commentId);
      return ok(null);
    }
    return api.delete(`/api/comments/tickets/${ticketId}/comments/${commentId}`);
  },
};

// ─── Email ────────────────────────────────────────────────────────────────────

export const emailApi = {
  send: (ticketId, { to, cc, subject, body, include_email_thread = false, files = [] }) => {
    if (isDemo()) {
      const ticket = store.tickets.find((t) => t.id === ticketId);
      const recipient = to || ticket?.sender_email || "";
      const ccLine = cc?.trim() ? `\nCC: ${cc}` : "";
      const c = {
        id: `cm-${Date.now()}`,
        ticket_id: ticketId,
        body: `📧 מייל ללקוח\n\nאל: ${recipient}${ccLine}\nנושא: ${subject}\n\n${body}`,
        is_internal: true,
        author_id: DEMO_USER.id,
        author_name: DEMO_USER.display_name,
        author: { display_name: DEMO_USER.display_name },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      store.comments[ticketId] = [...(store.comments[ticketId] ?? []), c];
      addHistory(ticketId, "email_sent_customer", `מייל נשלח ללקוח — ${subject}`);
      return ok(c);
    }
    if (files.length) {
      const formData = new FormData();
      formData.append("to", to || "");
      formData.append("cc", cc || "");
      formData.append("subject", subject);
      formData.append("body", body);
      formData.append("include_email_thread", String(include_email_thread));
      appendFiles(formData, files);
      return api.post(`/api/tickets/${ticketId}/send-email`, formData).then(() => null);
    }
    return api.post(`/api/tickets/${ticketId}/send-email`, {
      to, cc, subject, body, include_email_thread,
    }).then(() => null);
  },
  notifyTechnician: (ticketId) => {
    if (isDemo()) {
      const ticket = store.tickets.find((t) => t.id === ticketId);
      if (ticket?.technician) {
        addHistory(ticketId, "email_sent_technician",
          `הודעה נשלחה לטכנאי ${ticket.technician.first_name} ${ticket.technician.last_name}`);
      }
      return ok(null);
    }
    return api.post(`/api/tickets/${ticketId}/notify-technician`).then(() => null);
  },
};

// ─── Email thread ─────────────────────────────────────────────────────────────

export const emailThreadApi = {
  list: (ticketId) => {
    if (isDemo()) return ok([]);
    return api.get(`/api/tickets/${ticketId}/email-thread`).then((r) => r.data);
  },
};

// ─── Attachments ──────────────────────────────────────────────────────────────

export const attachmentsApi = {
  list: (ticketId) => {
    if (isDemo()) return ok([]);
    return api.get(`/api/attachments/tickets/${ticketId}`).then((r) => r.data);
  },
  getBlob: (id) => api.get(`/api/attachments/${id}/download`, { responseType: "blob" }).then((r) => safeBlob(r.data)),
  download: async (id, filename) => {
    const blob = await attachmentsApi.getBlob(id);
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ─── History ──────────────────────────────────────────────────────────────────

export const historyApi = {
  list: (ticketId) => {
    if (isDemo()) {
      const entries = [...(store.history[ticketId] ?? [])];
      return ok(entries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    }
    return api.get(`/api/tickets/${ticketId}/history`).then((r) => r.data);
  },
};

// ─── Backup ───────────────────────────────────────────────────────────────────

export const backupApi = {
  download: async (format) => {
    const today = new Date().toISOString().slice(0, 10);
    // Through the shared `api` instance (not a raw fetch), so an expired/cleared token gets the
    // same 401 interceptor handling (sign-out + redirect to /) every other authenticated request
    // gets, instead of throwing a generic "Backup download failed" error the caller can't tell
    // apart from the server actually being down.
    const blob = await api.get(`/api/backup/${format}`, { responseType: "blob" }).then((r) => safeBlob(r.data));
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `tickets_backup_${today}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ─── SharePoint ───────────────────────────────────────────────────────────────

export const sharepointApi = {
  search:  (query, ticket_id = null, folder_id = null) =>
    api.post("/api/sharepoint/search", { query, ticket_id, folder_id }).then((r) => r.data),
  folders: () =>
    api.get("/api/sharepoint/folders").then((r) => r.data),
};

// ─── AI ───────────────────────────────────────────────────────────────────────

export const aiApi = {
  suggest:    (ticketId) => api.post(`/api/tickets/${ticketId}/ai-suggest`).then((r) => r.data),
  getSettings: ()        => api.get("/api/settings/ai").then((r) => r.data),
  saveSettings: (data)   => api.put("/api/settings/ai", data),
};

// ─── Server Status ────────────────────────────────────────────────────────────

export const serverApi = {
  status:  ()  => api.get("/api/server/status").then((r) => r.data),
  refresh: ()  => api.post("/api/server/status/refresh").then((r) => r.data),
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settingsApi = {
  blocklist: {
    list:   ()         => api.get("/api/settings/blocklist").then((r) => r.data),
    add:    (data)     => api.post("/api/settings/blocklist", data).then((r) => r.data),
    remove: (id)       => api.delete(`/api/settings/blocklist/${id}`),
  },
  combinedBlocklist: {
    list:   ()         => api.get("/api/settings/blocklist-combined").then((r) => r.data),
    add:    (data)     => api.post("/api/settings/blocklist-combined", data).then((r) => r.data),
    remove: (id)       => api.delete(`/api/settings/blocklist-combined/${id}`),
  },
};

// ─── Projects ─────────────────────────────────────────────────────────────────

export const projectsApi = {
  dashboardStats:  ()              => api.get("/api/projects/dashboard-stats").then((r) => r.data),
  list:            (params)        => api.get("/api/projects/", { params }).then((r) => r.data),
  get:             (id)            => api.get(`/api/projects/${id}`).then((r) => r.data),
  create:          (data)          => api.post("/api/projects/", data).then((r) => r.data),
  update:          (id, data)      => api.put(`/api/projects/${id}`, data).then((r) => r.data),
  delete:          (id)            => api.delete(`/api/projects/${id}`),
  bulkDelete:       (ids)               => api.post("/api/projects/bulk-delete", { ids }),
  merge:            (primaryId, secondaryIds) => api.post("/api/projects/merge", { primary_id: primaryId, secondary_ids: secondaryIds }).then((r) => r.data),
  createMilestone:  (projectId, data)   => api.post(`/api/projects/${projectId}/milestones`, data).then((r) => r.data),
  listNotes:        (projectId)         => api.get(`/api/projects/${projectId}/notes`).then((r) => r.data),
  createNote:       (projectId, message, files = []) => {
    const form = buildProjectNoteFormData(message, files);
    return api.post(`/api/projects/${projectId}/notes`, form).then((r) => r.data);
  },
  updateNote:       (projectId, noteId, message) =>
    api.patch(`/api/projects/${projectId}/notes/${noteId}`, { message }).then((r) => r.data),
  deleteNote:       (projectId, noteId) => api.delete(`/api/projects/${projectId}/notes/${noteId}`),
  viewProjectNoteAttachment: (attachmentId) =>
    api.get(`/api/projects/note-attachments/${attachmentId}/view`, { responseType: "blob" }).then((r) => safeBlob(r.data)),
  downloadProjectNoteAttachment: (attachmentId) =>
    api.get(`/api/projects/note-attachments/${attachmentId}/download`, { responseType: "blob" }).then((r) => safeBlob(r.data)),
  createMilestoneUpdate: (milestoneId, message, files = []) => {
    const form = new FormData();
    form.append("message", message);
    appendFiles(form, files);
    return api.post(`/api/projects/milestones/${milestoneId}/updates`, form).then((r) => r.data);
  },
  viewMilestoneAttachment: (attachmentId) =>
    api.get(`/api/projects/attachments/${attachmentId}/view`, { responseType: "blob" }).then((r) => safeBlob(r.data)),
  downloadMilestoneAttachment: (attachmentId) =>
    api.get(`/api/projects/attachments/${attachmentId}/download`, { responseType: "blob" }).then((r) => safeBlob(r.data)),
  updateMilestone:  (milestoneId, data) => api.put(`/api/projects/milestones/${milestoneId}`, data).then((r) => r.data),
  completeMilestone:(milestoneId)       => api.put(`/api/projects/milestones/${milestoneId}/complete`).then((r) => r.data),
  deleteMilestone:  (milestoneId)       => api.delete(`/api/projects/milestones/${milestoneId}`),
  createTask:       (milestoneId, data) => api.post(`/api/projects/milestones/${milestoneId}/tasks`, data).then((r) => r.data),
  updateTask:       (taskId, data)      => api.put(`/api/projects/tasks/${taskId}`, data).then((r) => r.data),
  completeTask:     (taskId)            => api.put(`/api/projects/tasks/${taskId}/complete`).then((r) => r.data),
  deleteTask:       (taskId)            => api.delete(`/api/projects/tasks/${taskId}`),
};

// ─── Maintenance ──────────────────────────────────────────────────────────────

export const maintenanceApi = {
  dashboardStats: ()         => api.get("/api/maintenance/dashboard-stats").then((r) => r.data),
  list:           (params)   => api.get("/api/maintenance/", { params }).then((r) => r.data),
  get:            (id)       => api.get(`/api/maintenance/${id}`).then((r) => r.data),
  create:         (data)     => api.post("/api/maintenance/", data).then((r) => r.data),
  update:         (id, data) => api.put(`/api/maintenance/${id}`, data).then((r) => r.data),
  delete:         (id)       => api.delete(`/api/maintenance/${id}`),
  bulkDelete:     (ids)      => api.post("/api/maintenance/bulk-delete", { ids }),
  merge:          (primaryId, secondaryIds) => api.post("/api/maintenance/merge", { primary_id: primaryId, secondary_ids: secondaryIds }).then((r) => r.data),
};

// ─── Contacts ─────────────────────────────────────────────────────────────────

export const contactsApi = {
  list: (params) => {
    if (isDemo()) return ok([]);
    return api.get("/api/contacts/", { params }).then((r) => r.data);
  },
  get: (id) => {
    if (isDemo()) return ok(null);
    return api.get(`/api/contacts/${id}`).then((r) => r.data);
  },
  create: (data) => {
    if (isDemo()) return ok({ ...data, id: `ct-${Date.now()}`, created_at: new Date().toISOString() });
    return api.post("/api/contacts/", data).then((r) => r.data);
  },
  update: (id, data) => {
    if (isDemo()) return ok({ id, ...data });
    return api.patch(`/api/contacts/${id}`, data).then((r) => r.data);
  },
  delete: (id) => {
    if (isDemo()) return ok(null);
    return api.delete(`/api/contacts/${id}`);
  },
};

// ─── Managed Domains (independent from company email domains) ────────────────

export const managedDomainsApi = {
  list:   (params) => api.get("/api/managed-domains/", { params }).then((r) => r.data),
  create: (data)   => api.post("/api/managed-domains/", data).then((r) => r.data),
  update: (id, data) => api.put(`/api/managed-domains/${id}`, data).then((r) => r.data),
  delete: (id)     => api.delete(`/api/managed-domains/${id}`),
};

// ─── Agents ───────────────────────────────────────────────────────────────────

export const agentsApi = {
  list:   ()         => api.get("/api/agents/").then((r) => r.data),
  // PROD-BUILDER-059: the optional request config carries the per-attempt Idempotency-Key, so a
  // repeated submit is answered by the employee the first attempt created instead of a duplicate.
  create: (data, config) => api.post("/api/agents/", data, config).then((r) => r.data),
  get:    (id)       => api.get(`/api/agents/${id}`).then((r) => r.data),
  update: (id, data) => api.patch(`/api/agents/${id}`, data).then((r) => r.data),
  delete: (id)       => api.delete(`/api/agents/${id}`),
  listTools:          ()  => api.get("/api/agents/tools").then((r) => r.data),
  entitlementReadiness: () => api.get("/api/agents/entitlement-readiness").then((r) => r.data),
  // DIGEMP-DIGITAL-WORKFORCE-OVERVIEW-BACKEND-067: read-only fleet aggregate (agents_view) —
  // employees, present-tense workload/waits, windowed verified outcomes and attention needs.
  workforceOverview:  (params = {}) => api.get("/api/agents/workforce-overview", { params }).then((r) => r.data),
  workspace:          (id) => api.get(`/api/agents/${id}/workspace`).then((r) => r.data),
  // DIGEMP-SPECIALIST-ACRONIS-VERIFIED-READS-UX-221: the composed read-only Acronis readiness
  // contract — the verified adapter read slice with the exposure and evidence the server states,
  // beside the employee's already-entitled tools. Same agents_view gate as the workspace
  // projection above; grants nothing and is never a write path.
  acronisReadiness:   (id) => api.get(`/api/agents/${id}/acronis-readiness`).then((r) => r.data),
  // DIGEMP-MANAGER-ROUTING-DESK-FRONTEND-046: read-only ranking of eligible employees for a
  // free-text request. Assigns nothing — the desk hands the operator's own choice to the
  // existing conversation flow.
  routingRecommendation: (payload) => api.post("/api/agents/routing-recommendation", payload).then((r) => r.data),
  growthProfile:      (id) => api.get(`/api/agents/${id}/growth-profile`).then((r) => r.data),
  experienceHistory:  (id, params = {}) => api.get(`/api/agents/${id}/experience-history`, { params }).then((r) => r.data),
  getCrisisMode:      ()  => api.get("/api/agents/crisis-mode").then((r) => r.data),
  activateCrisisMode: ()  => api.post("/api/agents/crisis-mode/activate").then((r) => r.data),
  deactivateCrisisMode: () => api.post("/api/agents/crisis-mode/deactivate").then((r) => r.data),
  getAutoTrigger:     ()  => api.get("/api/agents/auto-trigger").then((r) => r.data),
  enableAutoTrigger:  ()  => api.post("/api/agents/auto-trigger/enable").then((r) => r.data),
  disableAutoTrigger: ()  => api.post("/api/agents/auto-trigger/disable").then((r) => r.data),
  // DIGEMP-PILOT-E2E-CLOSEOUT-095: explicit Browser Work request, wiring the
  // DIGEMP-BROWSER-WORK-FOUNDATION-ASAF-098 contract into the ticket surface. Persists nothing
  // server-side and never returns a fabricated success — see backend/app/browser_work/domain.py.
  requestBrowserWork: (agentId, payload) => api.post(`/api/agents/${agentId}/browser-work-request`, payload).then((r) => r.data),
  // DIGEMP-FINISH-BUILDER-PUBLISH-FRONTEND-143: the apply half of the Employee Builder v3
  // wizard — replays the wizard's skill/system intent against the created employee through the
  // governed backend publish path (DIGEMP-FINISH-BUILDER-PUBLISH-BACKEND-137). Same
  // agents_manage gate as create; the response labels every non-applied item explicitly and
  // grants nothing.
  builderPublish: (id, payload) => api.post(`/api/agents/${id}/builder-publish-v3`, payload).then((r) => r.data),
  // PROD-BUILDER-060: the two canonical reads the post-publish surface uses to show what the
  // employee's records ACTUALLY are after a publish, instead of trusting the publish response
  // as proof. Both routes already exist, are read-only and are gated agents_view; they never
  // resolve a credential and never write. A failed read is reported as unknown, never success.
  skillAssignmentState: (id) => api.get(`/api/agents/${id}/skill-assignment-state`).then((r) => r.data),
  connectionReadiness:  (id) => api.get(`/api/agents/${id}/connection-readiness`).then((r) => r.data),
  // PROD-REMOTECONTRACT-028: the §4.1/§4.2/§4.3 contract on the admin/owner-gated product path.
  // The GET reads the active run's ticket/customer binding beside the DEV-fixture profile and action
  // catalog; the POST records one catalog action as a broker receipt. Neither executes a remote
  // action, resolves a credential or creates an approval.
  remoteConnectionProfiles: (id) => api.get(`/api/agents/${id}/remote-operations/connection-profiles`).then((r) => r.data),
  remoteSessionReceipt: (id, payload) => api.post(`/api/agents/${id}/remote-operations/session-receipt`, payload).then((r) => r.data),
};

// ─── Agent Runs (AI Technician Pilot) ─────────────────────────────────────────

export const agentRunsApi = {
  start:           (ticket_id, agent_id, extra = {}) => api.post("/api/agent-runs/", { ticket_id, ...(agent_id ? { agent_id } : {}), ...extra }).then((r) => r.data),
  get:             (run_id)            => api.get(`/api/agent-runs/${run_id}`).then((r) => r.data),
  list:            (ticket_id)         => api.get("/api/agent-runs/", { params: { ticket_id } }).then((r) => r.data),
  listAll:         (params = {})       => api.get("/api/agent-runs/", { params }).then((r) => r.data),
  pendingApprovals:(params = {})       => api.get("/api/agent-runs/pending-approvals", { params }).then((r) => r.data),
  recentFailures:  (params = {})       => api.get("/api/agent-runs/recent-failures", { params }).then((r) => r.data),
  stats:           (params = {})       => api.get("/api/agent-runs/stats", { params }).then((r) => r.data),
  listByAgent:     (agent_id, params = {}) => api.get("/api/agent-runs/", { params: { agent_id, ...params } }).then((r) => r.data),
  // Read-only Digital Employee projections (same agents_view permission as every other read)
  workloadSummary: (params = {})       => api.get("/api/agent-runs/workload-summary", { params }).then((r) => r.data),
  // DIGEMP-AGENT-WORKLOAD-DETAIL-BACKEND-006: one employee's live runs grouped into the operator
  // buckets (actively handling / waiting for technician review / waiting for approval / waiting for
  // customer) plus the recent failures. Identifiers, statuses and timestamps only — the server
  // deliberately projects no analysis or prompt content here. Read-only; grants nothing.
  workloadDetail:  (agentId, params = {}) => api.get(`/api/agent-runs/workload-summary/${agentId}`, { params }).then((r) => r.data),
  teamQueueSummary:(params = {})       => api.get("/api/agent-runs/team-queue-summary", { params }).then((r) => r.data),
  systemCheck:     (payload)               => api.post("/api/agent-runs/system-check", payload).then((r) => r.data),
  approve:         (run_id)            => api.post(`/api/agent-runs/${run_id}/approve`).then((r) => r.data),
  reject:          (run_id, reason)    => api.post(`/api/agent-runs/${run_id}/reject`, { reason }).then((r) => r.data),
  feedback:        (run_id, payload)   => api.post(`/api/agent-runs/${run_id}/feedback`, payload).then((r) => r.data),
  rate:            (run_id, rating)    => api.post(`/api/agent-runs/${run_id}/rate`, { rating }),
  stop:            (run_id, reason)    => api.post(`/api/agent-runs/${run_id}/stop`, { reason }).then((r) => r.data),
  resume:          (run_id)            => api.post(`/api/agent-runs/${run_id}/resume`).then((r) => r.data),
  retry:           (run_id)            => api.post(`/api/agent-runs/${run_id}/retry`).then((r) => r.data),
  connectionActive:(run_id)            => api.post(`/api/agent-runs/${run_id}/connection-active`).then((r) => r.data),
  approvalTokens:  (run_id)            => api.get(`/api/agent-runs/${run_id}/approval-tokens`).then((r) => r.data),
  // The server's own preview of what an approval will actually do, including the human-readable
  // blocking conditions (Crisis Mode, the Adaptive Knowledge Gate) that make it a no-op.
  approvalContext: (run_id)            => api.get(`/api/agent-runs/${run_id}/approval-context`).then((r) => r.data),
  // Who/what this run is bound to (employee, ticket, customer, requesting user, operator, device,
  // proposed-action target), each with the server's own fail-closed status and provenance.
  contextBinding:  (run_id)            => api.get(`/api/agent-runs/${run_id}/context-binding`).then((r) => r.data),
  // MASTER_FLOW §5A.4 agent-run QA / decision inspector: the run's decision inputs with their
  // provenance, the findings they support, the evidence that is missing, and whether the outcome is
  // eligible to close without a human re-reading it. Read-only evidence, never authority.
  qaInspection:    (run_id)            => api.get(`/api/agent-runs/${run_id}/qa-inspection`).then((r) => r.data),
  feedbackHistory: (run_id)            => api.get(`/api/agent-runs/${run_id}/feedback`).then((r) => r.data),
  feedbackStats:   (sinceDays)         => api.get("/api/agent-runs/feedback/stats", sinceDays ? { params: { since_days: sinceDays } } : {}).then((r) => r.data),
  recentFeedback:  (sinceDays, limit)  => api.get("/api/agent-runs/feedback/recent", { params: { since_days: sinceDays ?? 30, limit: limit ?? 20 } }).then((r) => r.data),
  batchApprove:    (run_ids)           => api.post("/api/agent-runs/batch-approve", { run_ids }).then((r) => r.data),
  costStats:       (params = {})       => api.get("/api/agent-runs/stats/cost", { params }).then((r) => r.data),
  // YK48-ASAF-09 provider/model health, fallback and cost rollup (read-only; drives the
  // Model & Provider transparency page).
  providerHealth:  (params = {})       => api.get("/api/agent-runs/stats/provider-health", { params }).then((r) => r.data),
  trend:           (days = 7)          => api.get("/api/agent-runs/stats/trend", { params: { days } }).then((r) => r.data),
  statsByTaskType: (params = {})       => api.get("/api/agent-runs/stats/by-task-type", { params }).then((r) => r.data),
  statsByCompany:    (params = {})     => api.get("/api/agent-runs/stats/by-company",       { params }).then((r) => r.data),
  statsByTechnician: (params = {})     => api.get("/api/agent-runs/stats/by-technician",     { params }).then((r) => r.data),
  resolutionImpact:  (params = {})     => api.get("/api/agent-runs/stats/resolution-impact", { params }).then((r) => r.data),
};

// ─── Product evaluation evidence (FULL-0128 / PROD-OPS-042) ──────────────────
//
// MASTER_FLOW §15A's EVALUATE/IMPROVE surface. These four reads already shipped behind an
// Admin/Owner gate (FULL-0111 / FULL-0126) and had no frontend consumer. This group is
// deliberately GET-only: the recording, approval-minting and promotion routes
// (`record`, `approvals`, `promote`) are the explicit two-person operator path and are NOT
// mounted by the read-only workspace. `AgentEvaluationsPage.test.jsx` parses this object and
// fails if a non-GET member is added here.
export const productEvalApi = {
  contract:          ()             => api.get("/api/agents/product-eval/contract").then((r) => r.data),
  promotionContract: ()             => api.get("/api/agents/product-eval/promotion/contract").then((r) => r.data),
  evidence:          (params = {})  => api.get("/api/agents/product-eval/promotion/evidence", { params }).then((r) => r.data),
  promotions:        (params = {})  => api.get("/api/agents/product-eval/promotion/promotions", { params }).then((r) => r.data),
};

// ─── Conversations (HELPDESK-CONVERSATIONAL-WORKER-001) ──────────────────────

export const conversationsApi = {
  create:       (payload)                => api.post("/api/conversations/", payload).then((r) => r.data),
  get:          (conversationId)         => api.get(`/api/conversations/${conversationId}`).then((r) => r.data),
  list:         (params = {})            => api.get("/api/conversations/", { params }).then((r) => r.data),
  listMessages: (conversationId)         => api.get(`/api/conversations/${conversationId}/messages`).then((r) => r.data),
  postMessage:  (conversationId, payload) => api.post(`/api/conversations/${conversationId}/messages`, payload).then((r) => r.data),
  handoff:      (conversationId, payload) => api.post(`/api/conversations/${conversationId}/handoff`, payload).then((r) => r.data),
  trackRun:     (conversationId, runId)   => api.post(`/api/conversations/${conversationId}/runs/${runId}/track`).then((r) => r.data),
};

// ─── Conversation Center (DIGEMP-GLOBAL-CONVERSATIONS-BACKEND-072) ────────────
// Read-only operator projection over every Digital Employee conversation. The list and the
// single-thread read are assembled by the same backend function, so their metadata cannot diverge
// — the page and the thread header may show the same fields.

export const conversationCenterApi = {
  list: (params = {}) => api.get("/api/agents/conversation-center", { params }).then((r) => r.data),
  get:  (conversationId) => api.get(`/api/agents/conversation-center/${conversationId}`).then((r) => r.data),
};

// ─── Agent Permissions ────────────────────────────────────────────────────────

export const agentPermissionsApi = {
  me:     ()              => api.get("/api/agent-permissions/me").then((r) => r.data),
  users:  ()              => api.get("/api/agent-permissions/users").then((r) => r.data),
  grant:  (user_id, permission) => api.post("/api/agent-permissions/grant", { user_id, permission }),
  revoke: (user_id, permission) => api.post("/api/agent-permissions/revoke", { user_id, permission }),
};

// ─── Connections / Tool Registry ──────────────────────────────────────────────

export const connectionsApi = {
  health:       ()   => api.get("/api/connections/health").then((r) => r.data),
  testOpenclaw: ()   => api.post("/api/connections/test/openclaw").then((r) => r.data),
  // PROD-PROVIDERGUI-023: the admin preparation path for GUI-managed provider onboarding. Both are
  // read-only — the contract publishes the closed sets the form renders from, and the preview judges a
  // submitted draft. Neither provisions a credential nor enables a provider.
  providerOnboardingContract: () => api.get("/api/connections/provider-onboarding/contract").then((r) => r.data),
  previewProviderOnboarding: (draft) => api.post("/api/connections/provider-onboarding/preview", draft).then((r) => r.data),
  // FULL-0159 / PROD-UX-073: the two READ-ONLY projections of the provisioning path. The list
  // returns the safe record projection (enabled / activation / credential reference evidence,
  // never the reference text); the contract publishes the two-person boundary the management
  // surface must state. Neither method writes, enables or approves anything.
  providerOnboardingProviders: () => api.get("/api/connections/provider-onboarding/providers").then((r) => r.data),
  providerOnboardingProvisioningContract: () =>
    api.get("/api/connections/provider-onboarding/provisioning/contract").then((r) => r.data),
};

export const agentConnectionsApi = {
  create: (agentId, data) => api.post(`/api/agents/${agentId}/connections`, data).then((r) => r.data),
  remove: (agentId, connectionId) => api.delete(`/api/agents/${agentId}/connections/${connectionId}`),
  test: (agentId, connectionId) => api.post(`/api/agents/${agentId}/connections/${connectionId}/test`).then((r) => r.data),
  // CONNECTION-HUB-001: registry-driven connector metadata (no credentials) — drives the
  // connection form's type list and each connection row's capability badges.
  connectorTypes: () => api.get("/api/agents/connector-types").then((r) => r.data),
  // ESET-LEARN-001 / ESET-ALL-CAPABILITIES-001: metadata-only OpenAPI capability discovery.
  capabilities: (agentId, connectionId) => api.get(`/api/agents/${agentId}/connections/${connectionId}/capabilities`).then((r) => r.data),
  // ACRONIS-CONNECTOR-001 (verified handoff slice): read-only, bounded tenant discovery for an
  // admin to pick external_tenant_id from — Acronis-only, never applies a mapping itself.
  acronisTenants: (agentId, connectionId) => api.get(`/api/agents/${agentId}/connections/${connectionId}/acronis-tenants`).then((r) => r.data),
  // DIGEMP-ACRONIS-FLEET-MAPPING-FRONTEND-099 (backend 098): durable company -> Acronis tenant
  // binding under one partner connection. Local configuration only — the body carries the stable
  // tenant UUID, never a credential/secret/token; the connection's secret_ref stays the only
  // credential reference. Conflicting bindings are refused by the server with a 409 + Hebrew
  // operator-readable detail.
  mapAcronisTenant: (agentId, connectionId, companyId, externalTenantId) =>
    api.put(
      `/api/agents/${agentId}/connections/${connectionId}/acronis-tenant-mappings/${companyId}`,
      { external_tenant_id: externalTenantId },
    ).then((r) => r.data),
  unmapAcronisTenant: (agentId, connectionId, mappingId) =>
    api.delete(`/api/agents/${agentId}/connections/${connectionId}/acronis-tenant-mappings/${mappingId}`).then((r) => r.data),
  advisoryCustomers: (agentId, connectionId) =>
    api.get(`/api/agents/${agentId}/connections/${connectionId}/advisory/customers`).then((r) => r.data),
  advisoryGroupTreeDiagnostic: (agentId, connectionId) =>
    api.get(`/api/agents/${agentId}/connections/${connectionId}/advisory/group-tree-diagnostic`).then((r) => r.data),
  advisoryQuery: (agentId, connectionId, data) =>
    api.post(`/api/agents/${agentId}/connections/${connectionId}/advisory/query`, data).then((r) => r.data),
};

// ─── Customer Profiles ────────────────────────────────────────────────────────

export const customerDocumentsApi = {
  list: (companyId) => api.get(`/api/companies/${companyId}/documents`).then((r) => r.data),
  upload: (companyId, file) => {
    const data = new FormData();
    data.append("file", file);
    return api.post(`/api/companies/${companyId}/documents`, data).then((r) => r.data);
  },
  download: async (companyId, documentId, filename) => {
    const blob = await api.get(`/api/companies/${companyId}/documents/${documentId}/download`, { responseType: "blob" }).then((r) => safeBlob(r.data));
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    try {
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
    } finally {
      anchor.remove();
      URL.revokeObjectURL(url);
    }
  },
};

export const customerProfilesApi = {
  get:    (companyId)           => api.get(`/api/companies/${companyId}/profile`).then((r) => r.data),
  upsert: (companyId, data)     => api.put(`/api/companies/${companyId}/profile`, data).then((r) => r.data),
  list:   ()                    => api.get("/api/companies/profiles/all").then((r) => r.data),
  // ESET-POC-001: bounded device list (name/ip/type only), open to any agents_view technician —
  // unlike get()/upsert() above, which are admin-only (the full environment profile).
  devices: (companyId)          => api.get(`/api/companies/${companyId}/devices`).then((r) => r.data),
};

// ─── Customer long-term memory (MEM-LONGTERM-001) ─────────────────────────────

export const agentMemoryApi = {
  list:      (companyId, includeInactive = false) =>
    api.get("/api/agent-memory", { params: { company_id: companyId, include_inactive: includeInactive } }).then((r) => r.data),
  create:    (data)                     => api.post("/api/agent-memory", data).then((r) => r.data),
  verify:    (companyId, id)            => api.post(`/api/agent-memory/${id}/verify`, null, { params: { company_id: companyId } }).then((r) => r.data),
  correct:   (companyId, id, content)   => api.post(`/api/agent-memory/${id}/correct`, { content }, { params: { company_id: companyId } }).then((r) => r.data),
  deprecate: (companyId, id)            => api.post(`/api/agent-memory/${id}/deprecate`, null, { params: { company_id: companyId } }).then((r) => r.data),
  remove:    (companyId, id)            => api.delete(`/api/agent-memory/${id}`, { params: { company_id: companyId } }).then((r) => r.data),
};

// ─── Bug Hunter ───────────────────────────────────────────────────────────────

export const bugHunterApi = {
  scan:         (params = {}) => api.get("/api/bug-hunter/scan", { params }).then((r) => r.data),
  createTicket: (params = {}) => api.post("/api/bug-hunter/create-ticket", null, { params }).then((r) => r.data),
};

// ─── Platform Guardian ────────────────────────────────────────────────────────

export const platformGuardianApi = {
  status:       ()            => api.get("/api/platform-guardian/status").then((r) => r.data),
  run:          ()            => api.post("/api/platform-guardian/run").then((r) => r.data),
  findings:     (params = {}) => api.get("/api/platform-guardian/findings", { params }).then((r) => r.data),
  findingsSummary: ()        => api.get("/api/platform-guardian/findings/summary").then((r) => r.data),
  updateStatus: (id, s)       => api.patch(`/api/platform-guardian/findings/${id}/status`, { status: s }).then((r) => r.data),
  createTicket: (id)          => api.post(`/api/platform-guardian/findings/${id}/create-ticket`).then((r) => r.data),
};

// ─── Skills & Evaluations ─────────────────────────────────────────────────────

export const skillsApi = {
    leaderboard:      (params = {}) => api.get("/api/skills/leaderboard", { params }).then((r) => r.data),
    agentEvaluation:  (agentId, params = {}) => api.get(`/api/skills/agents/${agentId}/evaluation`, { params }).then((r) => r.data),
};

// ─── Skill Lifecycle (MASTER_FLOW §3 "GUI-managed Skill Pool") ────────────────

// ONE read and ONE write. The write names a lifecycle status and nothing else: the approver is the
// signed-in operator (the server resolves it from the session and there is no field for it here),
// and the moves are the server's own (`available_transitions`), so this client never computes a
// transition table of its own. A level is not reachable from here at any point — §28.5 moves one
// only through a human-reviewed evaluation, which no route performs.
export const skillLifecycleApi = {
    state:        (skillId)            => api.get(`/api/skill-lifecycle/${skillId}`).then((r) => r.data),
    changeStatus: (skillId, newStatus) => api.post(`/api/skill-lifecycle/${skillId}/status`, { new_status: newStatus }).then((r) => r.data),
};

// ─── Skill Management (MASTER_FLOW §3 assignment / unassignment) ──────────────

// The contract read is the server's own closed §28 domain vocabulary; the client keeps no copy.
// Assignment names a governed domain and nothing else, and unassignment is the server's terminal
// §28.4 deprecation move -- there is deliberately no delete, even though one would be easy to
// write here. The operator is the signed-in user (the server resolves it from the session), and
// no level, evaluation or version operation is reachable from this client.
export const skillManagementApi = {
    contract: ()              => api.get("/api/skill-management/contract").then((r) => r.data),
    assign:   (agentId, domain) => api.post(`/api/skill-management/${agentId}/assign`, { domain }).then((r) => r.data),
    unassign: (agentId, skillId) => api.post(`/api/skill-management/${agentId}/${skillId}/unassign`).then((r) => r.data),
};

// ─── Knowledge Gaps ───────────────────────────────────────────────────────────

export const knowledgeGapsApi = {
  list:           (params = {}) => api.get("/api/knowledge-gaps/", { params }).then((r) => r.data),
  listNeedsArticle: ()          => api.get("/api/knowledge-gaps/", { params: { needs_article: true } }).then((r) => r.data),
  updateStatus:   (id, status)  => api.patch(`/api/knowledge-gaps/${id}/status`, { status }).then((r) => r.data),
  createArticle:  (id)          => api.post(`/api/knowledge-gaps/${id}/create-article`).then((r) => r.data),
};

// ─── Adaptive Knowledge Gate (MASTER_FLOW §4.1) ───────────────────────────────
// The technician's half of the mismatch path. Four existing endpoints, no more: the run's current
// gate state, the run's whole append-only decision trail, recording an observed mismatch, and
// recording the targeted vendor research that can reconcile it. Nothing here executes, approves or
// grants anything — clearing a mismatch only re-permits an action a human already approved, which is
// why the research write needs agents_approve on the server and is offered in the UI only to a user
// who holds it.

export const knowledgeGateApi = {
  state:    (run_id)           => api.get(`/api/knowledge-gate/runs/${run_id}`).then((r) => r.data),
  history:  (run_id)           => api.get(`/api/knowledge-gate/runs/${run_id}/history`).then((r) => r.data),
  observe:  (run_id, payload)  => api.post(`/api/knowledge-gate/runs/${run_id}/observations`, payload).then((r) => r.data),
  research: (run_id, payload)  => api.post(`/api/knowledge-gate/runs/${run_id}/research`, payload).then((r) => r.data),
};

// ─── Knowledge Articles ───────────────────────────────────────────────────────

export const knowledgeApi = {
  list:       (params) => api.get("/api/knowledge/", { params }).then((r) => r.data),
    listUnused: (params = {}) => api.get("/api/knowledge/", { params: { ...params, unused_only: true, approved_only: true } }).then((r) => r.data),
  get:        (id)     => api.get(`/api/knowledge/${id}`).then((r) => r.data),
  create:     (data)   => api.post("/api/knowledge/", data).then((r) => r.data),
  update:     (id, d)  => api.patch(`/api/knowledge/${id}`, d).then((r) => r.data),
  approve:    (id)     => api.post(`/api/knowledge/${id}/approve`).then((r) => r.data),
  delete:     (id)     => api.delete(`/api/knowledge/${id}`),
};

// ─── Technology Scout ─────────────────────────────────────────────────────────

export const techScoutApi = {
  catalog:          ()             => api.get("/api/tech-scout/catalog").then((r) => r.data),
  status:           ()             => api.get("/api/tech-scout/status").then((r) => r.data),
  run:              ()             => api.post("/api/tech-scout/run").then((r) => r.data),
  discoveries:      (params = {})  => api.get("/api/tech-scout/discoveries", { params }).then((r) => r.data),
  attention:        ()             => api.get("/api/tech-scout/discoveries", { params: { status_filter: "pending", sort: "attention" } }).then((r) => r.data),
  ask:              (query)        => api.post("/api/tech-scout/ask", { query }).then((r) => r.data),
  addDiscovery:     (payload)      => api.post("/api/tech-scout/discoveries", payload).then((r) => r.data),
  decide:           (id, payload)  => api.patch(`/api/tech-scout/discoveries/${id}/decide`, payload).then((r) => r.data),
  // The external-Skill review path: a read-only projection over the discovery/ingestion pipeline.
  // `skillReviewContract` publishes every closed vocabulary the surface renders, so no screen holds
  // its own copy of a stage, a verdict or a rejection reason; `reviewSkillCandidate` asks the server
  // to review one discovery row against §3's admission chain and grants nothing either way.
  skillReviewContract:      ()                 => api.get("/api/tech-scout/skill-review/contract").then((r) => r.data),
  reviewSkillCandidate:     (id, declaration)  => api.post(`/api/tech-scout/skill-review/${id}`, declaration).then((r) => r.data),
};

// ─── Backup Guardian ──────────────────────────────────────────────────────────

export const backupGuardianApi = {
  health:    ()       => api.get("/api/backup-guardian/health").then((r) => r.data),
  check:     ()       => api.post("/api/backup-guardian/check").then((r) => r.data),
  getConfig: ()       => api.get("/api/backup-guardian/config").then((r) => r.data),
  setConfig: (data)   => api.put("/api/backup-guardian/config", data).then((r) => r.data),
};

// ─── Guardian Center (DIGEMP-GUARDIAN-CENTER-V2-BACKEND-087) ──────────────────

export const guardianCenterApi = {
  // The single read-only composed Guardian center: scan, findings, backup and connection
  // state plus the security audit trail and guardian escalation tickets, each with severity,
  // freshness, provenance, escalation state and the employees/work it relates to. Read-only —
  // every state-changing action stays on its own existing endpoint.
  get: () => api.get("/api/agents/guardian-center").then((r) => r.data),
};

// ─── Scheduled Checks ─────────────────────────────────────────────────────────

export const scheduledChecksApi = {
  list:    ()                  => api.get("/api/scheduled-checks/").then((r) => r.data),
  create:  (data)              => api.post("/api/scheduled-checks/", data).then((r) => r.data),
  update:  (id, data)          => api.put(`/api/scheduled-checks/${id}`, data).then((r) => r.data),
  remove:  (id)                => api.delete(`/api/scheduled-checks/${id}`),
  runNow:  (id)                => api.post(`/api/scheduled-checks/${id}/run-now`).then((r) => r.data),
};

// ─── Alerts ───────────────────────────────────────────────────────────────────

export const alertsApi = {
  evaluate:  ()       => api.get("/api/alerts/evaluate").then((r) => r.data),
  dispatch:  ()       => api.post("/api/alerts/dispatch").then((r) => r.data),
  getConfig: ()       => api.get("/api/alerts/config").then((r) => r.data),
  setConfig: (data)   => api.put("/api/alerts/config", data).then((r) => r.data),
};

// ─── Activity Log ─────────────────────────────────────────────────────────────

export const activityApi = {
  list: (params) => api.get("/api/activity-log/", { params }).then((r) => r.data),
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export const reportsApi = {
  byCompany:    ()                    => api.get("/api/reports/by-company").then((r) => r.data),
  byTechnician: ()                    => api.get("/api/reports/by-technician").then((r) => r.data),
  byTime:       (start, end)          => api.get("/api/reports/by-time", { params: { start, end } }).then((r) => r.data),
  summary:      ()                    => api.get("/api/reports/summary").then((r) => r.data),
  opsPulse:     (hours = 24)          => api.get("/api/reports/ops-pulse", { params: { since_hours: hours } }).then((r) => r.data),
  aiOperations: (days = 30)           => api.get("/api/reports/ai-operations", { params: { days } }).then((r) => r.data),
  alerts:       ()                    => api.get("/api/reports/alerts").then((r) => r.data),
  alertsConfig: ()                    => api.get("/api/reports/alerts/config").then((r) => r.data),
  setAlertsConfig: (data)             => api.put("/api/reports/alerts/config", data).then((r) => r.data),
  dispatch:     ()                    => api.post("/api/reports/alerts/dispatch").then((r) => r.data),
};

// ─── Pilot feedback capture + triage (FULL-0164 / PROD-UX-078) ────────────────
//
// Capture is open to every authenticated operator; classification is an explicit Admin/Owner
// decision on the server. These calls are metadata-only: they never approve, execute or grant.

export const pilotFeedbackApi = {
  list:   ()            => api.get("/api/pilot-feedback/").then((r) => r.data),
  create: (data)        => api.post("/api/pilot-feedback/", data).then((r) => r.data),
  triage: (id, data)    => api.patch(`/api/pilot-feedback/${id}/triage`, data).then((r) => r.data),
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  exchangeCode: (code, state) =>
    api.post("/api/auth/exchange", { code, state }).then((r) => r.data),
};

// ─── API Tokens ───────────────────────────────────────────────────────────────

export const apiTokensApi = {
  list:   ()         => api.get("/api/auth/tokens").then((r) => r.data),
  create: (data)     => api.post("/api/auth/tokens", data).then((r) => r.data),
  revoke: (id)       => api.delete(`/api/auth/tokens/${id}`),
};

// ─── Users ────────────────────────────────────────────────────────────────────

export const usersApi = {
  me:     ()         => { if (isDemo()) return ok(DEMO_USER); return api.get("/api/users/me").then((r) => r.data); },
  list:   ()         => api.get("/api/users/").then((r) => r.data),
  update: (id, data) => api.patch(`/api/users/${id}`, data).then((r) => r.data),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const notificationsStreamToken = () => api.post("/api/notifications/stream-token").then((r) => r.data.token);
export const notificationsStreamUrl = (token) => `${API_BASE_URL}/api/notifications/stream?token=${encodeURIComponent(token || "")}`;

export const notificationsApi = {
  list:          (params) => { if (isDemo()) return ok([]);        return api.get("/api/notifications", { params }).then((r) => r.data); },
  unreadCount:   ()       => { if (isDemo()) return ok({ unread_count: 0 }); return api.get("/api/notifications/unread-count").then((r) => r.data); },
  markRead:      (id)     => api.post(`/api/notifications/${id}/read`),
  markAllRead:   ()       => api.post("/api/notifications/read-all").then((r) => r.data),
  getPreferences:()       => api.get("/api/notifications/preferences").then((r) => r.data),
  updatePreference: (data) => api.put("/api/notifications/preferences", data).then((r) => r.data),
  updateDesktopSettings: (mode) => api.put("/api/notifications/desktop-settings", { desktop_display_mode: mode }).then((r) => r.data),
};

export default api;
